// Display helpers ported from the prototype's icons.jsx.
//
// Date arithmetic takes an optional "today" override for tests; every
// real call site reads live API data (useFacilityDashboard), so the
// default is the real wall clock, same as lastSeenLabel below.

import { CONSULTA_DEL_PROPIETARIO } from "@bitacorapro/api-types";
import type { IntakeChannel } from "@bitacorapro/core-engine";
import type { FindingTimelineActor, OrgMember, RecipeMeResponse } from "@bitacorapro/api-types";
import { formatRelativeAgo } from "@bitacorapro/shared/relative-time";
import { ApiError } from "./api/error";

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CL", { day: "2-digit", month: "short" }).replace(".", "");
}

// Absolute date + time-of-day, es-CL — the finding timeline's
// exact-instant complement to formatRelativeAgo's "hace X" label.
// Relative time alone collapses same-day events; an auditor needs to
// tell them apart, and a screen reader needs a full, unambiguous value
// rather than an abbreviated month that some TTS engines mis-read.
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

// Shared client-side id -> displayName lookup (ADR-0085 Part 2, Option C
// — core-api never resolves auth_users; the dashboard resolves against
// its own cached GET /auth/memberships roster instead). Kept here, not
// inlined per call site, so every surface shares one fallback string.
export function resolveUserDisplayName(
  userId: string,
  rosterByUserId: ReadonlyMap<string, Pick<OrgMember, "displayName">>
): string {
  return rosterByUserId.get(userId)?.displayName ?? "Usuario desconocido";
}

// Finding-timeline actor label. If ADR-0085 Part 2 ever flips to
// server-side resolution, the `kind: "user"` branch of
// FindingTimelineActor gains `displayName` directly and this function's
// `user` case becomes `actor.displayName ?? "Usuario desconocido"` —
// drop the `rosterByUserId` parameter here and at the one call site.
export function describeTimelineActor(
  actor: FindingTimelineActor,
  rosterByUserId: ReadonlyMap<string, Pick<OrgMember, "displayName">>
): string {
  if (actor.kind === "system") return "Sistema";
  if (actor.kind === "share_link") return "Proveedor · vía enlace";
  // `self` y `organization` son la proyección que el servidor hace para
  // el carril `asset_party` (la PWA del propietario). El dashboard no
  // debería recibirlos nunca —su lane es de operador y lee el actor
  // crudo—, pero la unión es la misma y el exhaustive check obliga a
  // resolverlos. Se tratan literalmente, sin roster: es justamente el
  // caso donde no hay un userId que resolver.
  if (actor.kind === "self") return "Tú";
  if (actor.kind === "organization") return actor.name;
  return resolveUserDisplayName(actor.userId, rosterByUserId);
}

export function daysSince(iso: string | null | undefined, today: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((today.getTime() - d.getTime()) / 86400000);
}

// Derive the "semáforo" state from open findings count + last-visit
// recency. Matches the prototype's threshold ladder.
//
// ADR-0137: "ok" here still means "no open observations", not "the last
// acta recorded passing items" — the semáforo reads only negatives. The
// positive side (per-section conformance from GET /assets/:id/state) is
// shown next to it on the store detail, not folded into this status;
// changing that is its own decision.
export type StoreStatus = "ok" | "warn" | "crit" | "idle";

export function deriveStoreStatus(args: {
  openFindingsCount: number;
  lastVisit: string | null | undefined;
  idleAfterDays: number | undefined;
  today?: Date;
}): StoreStatus {
  // Every real call site now threads the org's actual configured
  // alDiaThresholdDays (GET /recipes/me, ADR-0027 H-02) through, so this
  // fallback only fires while that query hasn't resolved yet. It mirrors
  // AL_DIA_DEFAULT_DAYS in apps/core-api/src/routes/recipes.ts — the
  // server-side canonical default — rather than inventing a fourth number
  // (#0384).
  const idle = args.idleAfterDays ?? 30;
  const ageDays = daysSince(args.lastVisit, args.today);
  // Open findings win over visit recency — a store with observations
  // reads "Con observaciones", never "Sin reporte reciente" (founder
  // decision 2026-10-22). As a consequence this derivation never yields
  // "crit"; the member stays in StoreStatus for its existing consumers.
  if (args.openFindingsCount > 0) return "warn";
  // A null lastVisit means "never visited" — always idle, regardless of
  // the idleAfterDays threshold. Only a non-null age past the threshold
  // also earns "idle".
  if (ageDays === null || ageDays > idle) return "idle";
  return "ok";
}

export function statusLabel(s: StoreStatus): string {
  return {
    ok: "En orden",
    warn: "Con observaciones",
    crit: "Crítico",
    idle: "Sin reporte reciente",
  }[s];
}

// Filter to idle stores (never visited, or stale past the threshold used
// to derive status) and sort with the most overdue first: never-visited
// stores rank ahead of any dated-but-stale visit.
export function selectIdleStores<
  T extends { status: StoreStatus; lastVisit: string | null | undefined },
>(assets: readonly T[], today: Date = new Date()): T[] {
  return assets
    .filter((a) => a.status === "idle")
    .slice()
    .sort((a, b) => {
      const da = daysSince(a.lastVisit, today);
      const db = daysSince(b.lastVisit, today);
      if (da === null && db === null) return 0;
      if (da === null) return -1;
      if (db === null) return 1;
      return db - da;
    });
}

// Recipe severity slugs in this org are "critica" / "moderada" / "leve"
// (per seed-facility/seed-dashboard-demo). The dashboard renders them
// through this label/class map; pill classes come from dashboard.css.
export type Severity = "critica" | "moderada" | "leve" | (string & {});

export function sevLabel(s: Severity): string {
  if (s === "critica") return "Crítica";
  if (s === "moderada") return "Moderada"; // fitness-allow: postventa-labels-single-source — facility, vocabulario propio de esa receta
  if (s === "leve") return "Leve"; // fitness-allow: postventa-labels-single-source — facility, vocabulario propio de esa receta
  return s;
}

// Gate de confianza (D2/D3/D5) — the `origen` the server derives from the
// submitter's role / the capture flow. Labels are presentation only; the
// enum lives in the engine (`INTAKE_CHANNELS`, ADR-0163).
export type ObservationOrigen = IntakeChannel;

export function origenLabel(o: ObservationOrigen | null | undefined): string {
  if (o === "acta") return "Acta de entrega";
  if (o === "observacion_suelta") return "Observación suelta";
  if (o === "reporte_propietario") return "Reporte del propietario";
  // ADR-0163 — la consulta que el jefe convirtió en observación.
  if (o === "consulta_propietario") return CONSULTA_DEL_PROPIETARIO;
  return "—";
}

export function sevPillClass(s: Severity): string {
  if (s === "critica") return "st-pill--crit";
  if (s === "moderada") return "st-pill--warn";
  if (s === "leve") return "st-pill--low";
  return "st-pill--idle";
}

// ADR-0134: the delegation helpers (estadoLabel / estadoPillClass /
// isOpenDelegation over `delegation_status`, ADR-0074) are gone — facility
// runs the observation thread and reads its state through
// lib/postventa/entryThread.ts. The column survives read-only until the
// contract migration (#0723).

// Per-template freshness (ADR-0068, #0393) — generalizes
// deriveStoreStatus's single org-wide alDiaThresholdDays to a
// per-template cadenceDays value read straight off the template row
// (GET /assets/:assetId/checklist-templates), not a hardcode.
//
// Returns null when cadenceDays is null: that is a deliberate
// per-template opt-out from ADR-0068 ("no scheduling/enforcement,
// purely informational"), not a missing-data state — callers must
// render nothing for that template's pill slot, never a placeholder.
export type DueState = "current" | "overdue";

export function deriveDueState(args: {
  lastReviewedAt: string | null;
  cadenceDays: number | null;
  today?: Date;
}): DueState | null {
  if (args.cadenceDays === null) return null;
  const ageDays = daysSince(args.lastReviewedAt, args.today);
  // Never reviewed = always overdue once a cadence is configured.
  if (ageDays === null) return "overdue";
  return ageDays >= args.cadenceDays ? "overdue" : "current";
}

export function dueStateLabel(s: DueState): string {
  return s === "current" ? "Al día" : "Vencido";
}

// Discriminated union, not a nullable dueDate — a never-serviced item has
// no date to derive at all, and the union makes that state unrepresentable
// as a fabricated one. "never-serviced" carries no tone: it renders via
// the literal st-pill--idle/st-dot--idle classes (ADR-0083 design-expert
// ruling), never through tone-interpolation, so the "idle" spelling is
// never load-bearing here the way it would be on a widened tone union.
export type MaintUrgency =
  | { status: "never-serviced" }
  | { status: "scheduled"; dueDate: Date; tone: "ok" | "warn" | "crit"; daysLabel: string };

// Maintenance-item urgency for the "Mantenciones" panel (ADR-0083).
// Deliberately separate from deriveDueState above: that one is a binary
// current/overdue read for a checklist's own freshness (DuePill,
// st-pill--due-*), this one is a 3-tier countdown to a hard due date,
// reusing the same st-pill--ok/warn/crit and st-dot--ok/warn/crit
// vocabulary the SLA and severity pills already use elsewhere in this app
// rather than inventing a fourth color family. The 30-day "por vencer"
// window mirrors the provider default SLA used the same way in slaCell —
// the one other place this codebase already draws a line between "soon"
// and "fine".
//
// Discriminates on `lastServicedAt === null`, never on `cadenceDays` — a
// maintenance item's cadenceDays is NOT NULL (ADR-0083 Decision 2, unlike
// checklist_templates' optional field this function originally served),
// so a `cadenceDays !== null` guard at the call site would never protect
// a never-serviced item from this function's date arithmetic. The
// earlier version of this function fabricated `dueDate = today +
// cadenceDays` for that case, rendering a false "Vigente" pill on
// equipment with no service record at all — the bug ADR-0083 exists not
// to repeat.
// Whole calendar days from `a` to `b`, ignoring time-of-day and immune to
// DST — normalizes both to a UTC midnight built from their *local*
// y/m/d components before differencing, so a DST hour shift between the
// two dates never perturbs the day count the way a raw
// (b.getTime() - a.getTime()) / 86400000 would.
function daysBetweenCalendarDates(a: Date, b: Date): number {
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((utcB - utcA) / 86400000);
}

export function deriveMaintUrgency(args: {
  lastServicedAt: string | null;
  cadenceDays: number;
  today?: Date;
}): MaintUrgency {
  if (!args.lastServicedAt) return { status: "never-serviced" };
  const today = args.today ?? new Date();
  const dueDate = new Date(args.lastServicedAt);
  dueDate.setDate(dueDate.getDate() + args.cadenceDays);
  // Calendar-day difference via UTC-normalized midnights, not raw
  // ms/86400000 on the two Date instants — the latter is off by one
  // whenever a DST transition falls between `today` and `dueDate` (a
  // real case for Chile's clock changes), since setDate operates on
  // local wall-clock time while the ms diff sees the lost/gained hour.
  // Caught by this function's own tests running in America/Santiago.
  const left = daysBetweenCalendarDates(today, dueDate);
  if (left < 0) {
    return {
      status: "scheduled",
      dueDate,
      tone: "crit",
      daysLabel: `Vencido hace ${Math.abs(left)} d`,
    };
  }
  if (left <= 30) {
    return { status: "scheduled", dueDate, tone: "warn", daysLabel: `Vence en ${left} d` };
  }
  return { status: "scheduled", dueDate, tone: "ok", daysLabel: `Vence en ${left} d` };
}

const MONTH_ABBR_ES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
] as const;

// "02-jun-2026" — the Mantenciones panel's date format. Distinct from
// fmtDate (day + month only, space-separated, no year): a maintenance
// due date needs the year since cadences run 6-24 months out, unlike
// the visit/finding dates fmtDate serves, which are always recent.
export function fmtMaintDate(d: Date): string {
  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTH_ABBR_ES[d.getMonth()];
  return `${day}-${month}-${d.getFullYear()}`;
}

// Effective-brand resolution chain (ADR-0033 §C2), client-side mirror
// of core-api's `resolveBrandsByAssetId` — an asset carries its own
// brandId when set directly, otherwise falls back to its asset group's
// brandId. Needed so the checklist-template store picker (#0424) can
// filter candidates to a template's brand and make
// CHECKLIST_TEMPLATE_BRAND_MISMATCH structurally unreachable, rather
// than caught after a failed PUT/PATCH.
export function effectiveBrandId(
  asset: { brandId: string | null; assetGroupId: string | null },
  groups: readonly { id: string; brandId: string | null }[]
): string | null {
  if (asset.brandId) return asset.brandId;
  if (!asset.assetGroupId) return null;
  return groups.find((g) => g.id === asset.assetGroupId)?.brandId ?? null;
}

// ADR-0058 observationStatuses (broker_postventa) — a separate
// vocabulary from delegationStatus above (no provider involved).
// Recipe-driven, never a hardcoded key comparison: the label comes off
// `recipe.observationStatuses[]`, the tone is derived from `terminal`
// plus the status's position in the recipe's own array, matching the
// SvelteKit FE's observationStatusVisualFor bucketing (§0.4 — no
// recipe.slug branches, no vertical-specific status-key checks here).
type ObservationStatusLike = {
  key: string;
  label: string;
  terminal: boolean;
  /** Recipe-declared pill tone (ADR-0129 hardening) — the recipe names
   *  its own visual identity so no client hardcodes a vertical's status
   *  keys. Absent → label-sentiment heuristic below. */
  tone?: string | undefined;
};

export function observationStatusLabel(
  statusKey: string | null,
  statuses: readonly ObservationStatusLike[]
): string {
  if (!statusKey) return "Sin estado";
  return statuses.find((s) => s.key === statusKey)?.label ?? statusKey;
}

// Tone resolution (code-reviewer hardening, ola 2): the recipe declares
// each status's tone (`observationStatuses[].tone`) so NO client carries
// a key→tone map of any vertical's vocabulary — a hardcoded map here was
// slug-branching with one extra step, invisible to the fitness scanner.
// The pre-graph label-sentiment heuristic below stays as the fallback
// for vocabularies that don't declare tones.
export function observationStatusPillClass(
  statusKey: string | null,
  statuses: readonly ObservationStatusLike[]
): string {
  if (!statusKey) return "st-pill--idle";
  const index = statuses.findIndex((s) => s.key === statusKey);
  const status = index >= 0 ? statuses[index] : undefined;
  if (status?.tone) return `st-pill--${status.tone}`;
  if (!status) return "st-pill--idle";
  if (status.terminal) {
    // Terminal statuses split by label sentiment — a recipe rarely
    // declares more than one "closed, rejected" key alongside one
    // "closed, resolved" key, and this stays a label heuristic rather
    // than a hardcoded key comparison so a new recipe's vocabulary
    // renders sensibly with zero FE changes.
    const rejected = /rechaz|anula|declin/i.test(status.label);
    return rejected ? "st-pill--crit" : "st-pill--ok";
  }
  // Non-terminal: first entry in the recipe's own ordering reads as the
  // "just opened" neutral state; every other non-terminal status reads
  // as "in progress."
  return index === 0 ? "st-pill--warn" : "st-pill--info";
}

// Client-side mirror of the engine's resolveMemberRoleActor, for
// DISPLAY GATING only (which actions to render) — the server re-checks
// on every write. Non-throwing on purpose: an undeclared role renders
// no lifecycle controls instead of erroring the screen.
export type ObservationActorLane =
  | "session_user"
  | "assigning_party"
  | "share_link"
  | "asset_party";

export function observationActorLane(
  recipe:
    | {
        memberRoles: readonly {
          key: string;
          // `| undefined` explícito por exactOptionalPropertyTypes: el
          // tipo del wire declara la propiedad como opcional-o-undefined.
          observationStatusActor?: ObservationActorLane | undefined;
        }[];
      }
    | undefined,
  role: string | undefined
): ObservationActorLane | null {
  if (!recipe || !role) return null;
  const found = recipe.memberRoles.find((r) => r.key === role);
  if (!found) return null;
  return found.observationStatusActor ?? "session_user";
}

// Recipe-driven plural resolution ("1 unidad" / "3 unidades") — Spanish
// only needs the count===1 rule. Recipe carries `asset`/`assetPlural`
// and `assetGroup`/`assetGroupPlural` pairs; this is the one place that
// picks between them so three screens don't each write the same ternary.
export function pluralizeLabel(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

// ADR-0098: facility closes actas by unilateral attestation, never
// signature — "firmada(s)" copy is false for that vertical. Every other
// vertical still closes by signature, so this reads the recipe rather
// than branching per-screen. Returns a lowercase adjective ready to
// drop into a sentence ("Las visitas {word} alimentan...", "el acta
// {word}") — `plural` picks agreement, default singular.
export function closedActaAdjective(
  recipe: Pick<RecipeMeResponse, "reportClosure" | "labels"> | null | undefined,
  plural = false
): string {
  // #0509 landed the `reportClosure` recipe primitive (ADR-0098
  // Decision 1), so the interim slug branch (and its fitness-allow)
  // is gone — this now keys on the primitive, per ADR-0098's own
  // postscript commitment.
  if ((recipe?.reportClosure ?? "signature") === "attestation") {
    return plural
      ? recipe!.labels.reportAttestedWordPlural.toLowerCase()
      : recipe!.labels.reportAttestedWord.toLowerCase();
  }
  return plural ? "firmadas" : "firmada";
}

// Relative "última actividad" label anchored to wall-clock now — the
// heartbeat at auth-svc /auth/refresh writes a real-time timestamp, so
// the dashboard reads it the same way. Delegates the "hoy / ayer /
// hace X días / hace X semanas" ladder to the shared formatter (#0388)
// and only adds this screen's own two conventions on top: a capitalized
// first letter (this reads as a standalone table cell, not a trailing
// clause), and a fallback to the absolute short date for anything 30+
// days old or in the future — the shared formatter would otherwise keep
// going into months/years, which this column has never shown. Takes an
// optional "today" override for tests, same convention as daysSince.
export function lastSeenLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "Nunca";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const days = daysSince(iso, now);
  if (days === null || days < 0 || days >= 30) return fmtDate(iso);
  const label = formatRelativeAgo(iso, now);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// Store identity, shared by every screen that names a tienda. The canonical
// client xlsx no longer guarantees a Dirección column (ADR-0020 Phase 6), so
// `unit` is the primary identity when present ("Local 12") and `address`
// only takes over when there is no unit. Centralized here so the list, the
// detail header, the kanban card, the filter option and the AssignDrawer
// never disagree about which of the two fields is the title — the earlier
// per-screen `address ?? unit` / `unit ?? address` chains did.
export function storeTitle(
  a: { unit: string | null; address: string | null },
  fallback = "—"
): string {
  return a.unit ?? a.address ?? fallback;
}

// The secondary line under storeTitle. Only meaningful when the unit is
// the title (then the address is extra context); when the address is
// already the title there is nothing left to show, so null — callers
// filter it out rather than rendering a duplicate.
export function storeSubtitle(a: { unit: string | null; address: string | null }): string | null {
  return a.unit ? a.address : null;
}

// Accent- and case-insensitive search key. Strips combining marks after
// NFD so "bano" matches "Baño" and "climatizacion" matches
// "Climatización" — dashboard users type fast and rarely reach for the
// tilde. Applied to both the haystack and the query.
export function normalizeSearch(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

// A sentence ends at ./!/? only when what follows starts a new sentence
// (uppercase, ¿, ¡, a digit) or the text ends. "frente a la Av. principal"
// has a lowercase continuation, so the period is an abbreviation, not an
// end — es-CL observations are full of "Av.", "Temp.", "aprox.", "Nº".
const SENTENCE_END_RE = /[.!?]+(?=\s+[\p{Lu}¿¡0-9]|\s*$)/gu;
// Abbreviations that are routinely followed by a capitalized word ("El Sr.
// González", "Av. Apoquindo", "Dra. Pérez"), which the lookahead above
// cannot tell apart from a real sentence boundary.
const ABBREVIATIONS = new Set([
  "sr",
  "sra",
  "srta",
  "dr",
  "dra",
  "av",
  "avda",
  "ud",
  "uds",
  "nº",
  "n°",
  "no",
  "art",
  "pág",
  "aprox",
  "temp",
  "cía",
  "ltda",
  "spa",
  "s.a",
  "e.i.r.l",
  "etc",
]);
const SUMMARY_MAX_LEN = 80;
// A "sentence" shorter than this is almost always a lead-in ("Urgente.",
// "Ojo.") rather than a headline worth stopping at.
const MIN_SENTENCE_LEN = 25;

function endsWithAbbreviation(head: string): boolean {
  const word = head.match(/([\p{L}\p{N}.°º]+)[.!?]+$/u)?.[1] ?? "";
  return ABBREVIATIONS.has(word.replace(/\.$/u, "").toLowerCase());
}

// One-line title for an observation. Text that already fits is returned
// whole; otherwise the first real sentence wins when it fits, and failing
// that the cut lands on the last word boundary under maxLen with an
// ellipsis. Null for empty/whitespace input so callers can pick their own
// fallback ("Observación", "—"). The full text still travels alongside
// (sr-only span, detail sub-line, CSV export) — this only shapes what
// reads as the row's headline.
export function summarize(
  text: string | null | undefined,
  maxLen = SUMMARY_MAX_LEN
): string | null {
  const trimmed = text?.trim();
  if (!trimmed) return null;
  if (trimmed.length <= maxLen) return trimmed;
  SENTENCE_END_RE.lastIndex = 0;
  for (const match of trimmed.matchAll(SENTENCE_END_RE)) {
    const end = match.index + match[0].length;
    if (end > maxLen) break;
    const head = trimmed.slice(0, end).trim();
    if (end >= MIN_SENTENCE_LEN && !endsWithAbbreviation(head)) return head;
  }
  const cut = trimmed.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(" ");
  const boundary = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${boundary}…`;
}

export type ObservationHeadlineInput = {
  /** Server-authored few-word title (api-types carries it on both
   *  facilityDashboardEntry and assetObservationSchema, nullable with a
   *  null default). Required here so every call site is forced to pass
   *  the wire field through rather than silently dropping the tier. */
  aiTitle: string | null;
  humanDescription: string | null;
  aiDescription: string | null;
  element: string | null;
  areaName: string | null;
};

export type ObservationHeadline = {
  text: string;
  tier: "title" | "summary" | "element" | "literal";
  /** Longest free-text description, never aiTitle; null on the literal tier. */
  fullText: string | null;
};

// Precedence: server-authored few-word title → first sentence of the
// longest description (element only when both descriptions are null) →
// area-qualified literal. `element` never beats a present description:
// on live-capture rows it is a one-word classification ("Muro"), and the
// client has no honest discriminator for checklist rows — if the label
// should be the headline there, the materializer writes it into aiTitle.
export function observationHeadline(o: ObservationHeadlineInput): ObservationHeadline {
  const fullText = o.humanDescription ?? o.aiDescription ?? o.element ?? null;
  const title = o.aiTitle?.trim();
  if (title) return { text: title, tier: "title", fullText };
  const summary = summarize(fullText);
  if (summary) {
    const tier = o.humanDescription == null && o.aiDescription == null ? "element" : "summary";
    return { text: summary, tier, fullText };
  }
  const area = o.areaName?.trim();
  return { text: area ? `Observación en ${area}` : "Observación", tier: "literal", fullText: null };
}

// Generic-error copy for the observación detail screen (DetalleObservacion's
// third error branch — 404/403 are handled separately with their own fixed
// copy). Deliberately never renders `error.message` raw to the user: the
// backend's v3 envelope can carry a pretty-printed JSON blob (the bug this
// helper exists to shield against), so the raw text is confined to
// `detail`, which the caller only surfaces inside a <details> disclosure.
export type FindingErrorCopy = {
  kind: "network" | "server";
  heading: string;
  sub: string;
  reference: string; // always-visible mono reference line, e.g. "Código: NETWORK"
  detail: string; // full technical detail, only shown inside a <details> disclosure
};

export function describeFindingErrorCopy(error: unknown): FindingErrorCopy {
  if (error instanceof ApiError && error.code === "NETWORK") {
    return {
      kind: "network",
      heading: "No pudimos conectarnos",
      sub: "Parece que tu conexión a internet se interrumpió. Revisa tu conexión e inténtalo de nuevo.",
      reference: "Código: NETWORK",
      detail: error.message.slice(0, 2000),
    };
  }
  const status = error instanceof ApiError ? error.status : null;
  const code = error instanceof ApiError ? error.code : "UNKNOWN";
  const message = error instanceof Error ? error.message : String(error);
  return {
    kind: "server",
    heading: "No pudimos cargar esta observación",
    sub: "Tuvimos un problema en nuestro sistema al traer esta información. No es algo que debas resolver tú — tus otras observaciones siguen disponibles.",
    reference: status !== null ? `Código: ${code} · HTTP ${status}` : `Código: ${code}`,
    detail: `${status ?? "—"} ${code}\n${message.slice(0, 2000)}`,
  };
}
